import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@staff-app/ui';
import { ADMIN_ROLES } from '@staff-app/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { PendingApprovalPage } from '@/pages/PendingApprovalPage';
import {
  useHardwareProducts,
  useHardwareCatalogForMapping,
  useUpsertHardwareProduct,
  useReorderHardwareProducts,
  useDeleteHardwareProduct,
  useHardwareShippingTiers,
  useUpsertHardwareShippingTier,
  useDeleteHardwareShippingTier,
  type HardwareProductMapping,
  type HardwareShippingTier,
} from '../hooks/useHardwareProducts';
import {
  useStripeCoupons,
  useUpsertStripeCoupon,
  useDeleteStripeCoupon,
  type StripeCouponCatalogEntry,
} from '../hooks/useStripeCoupons';
import {
  useOtherStripeCoupons,
  useUpsertOtherCoupon,
  useDeleteOtherCoupon,
  type OtherCouponCatalogEntry,
} from '../hooks/useOtherStripeCoupons';
import { Boxes } from 'lucide-react';

/**
 * Stable per-configuration identity: `sku` for a catalog-priced row, `sku|price_…` for a
 * Stripe-priced configuration. Must match the API's `optionKey()` — it is the key the
 * reorder mutation and the invoice picker both address rows by.
 */
const rowKey = (r: { sku: string; stripePriceId: string }) =>
  r.stripePriceId ? r.sku + '|' + r.stripePriceId : r.sku;

/** How a row is named in toasts. */
const label = (r: { sku: string; stripePriceNickname?: string | null }) =>
  r.stripePriceNickname ? r.sku + ' (' + r.stripePriceNickname + ')' : r.sku;

/** Minor units → a plain money string. `0` reads as "free", which is the point of it. */
const money = (minor: number, currency: string) =>
  minor === 0
    ? 'free'
    : (minor / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: (currency || 'usd').toUpperCase(),
      });

/**
 * "1 → $100.00 · 2 → $150.00 · 3+ → free" — the whole band ladder for one product on one
 * line, so an operator can check the shape at a glance rather than reading rows.
 */
const ladder = (tiers: HardwareShippingTier[]) =>
  [...tiers]
    .sort((a, b) => a.minQuantity - b.minQuantity)
    .map((t, i, all) => {
      const open = i === all.length - 1;
      return `${t.minQuantity}${open ? '+' : ''} → ${money(t.amountMinor, t.currency)}`;
    })
    .join(' · ');

/**
 * Administration → Hardware Products (root-only). Sidebar label: "Products List".
 *
 * Decides which device-catalog products the Billing Console's Create Hardware Only Invoice
 * tool can bill, which EXISTING Stripe product each one bills against, and the ORDER the
 * invoice picker lists them in. The left column is the Mongo `deviceProducts._id`.
 *
 * MAPPING ONLY, by design: there is no price field anywhere on this page. Prices
 * are read live from Mongo's `deviceProducts` when an invoice is raised (or from the
 * row's Stripe price), so this table can never become a second, drifting price list.
 * The Order column is a display position — the one editable thing here that cannot
 * change an amount.
 *
 * Rows are per environment — sandbox holds Stripe TEST product ids, prod holds
 * live ones — which is what makes the same product resolve correctly in both.
 *
 * Upstream routing guard: `<AdminRoleRoute requiredRole="root">` already blocks
 * non-root users; this component re-checks defensively, matching AdminUsersPage.
 */
export function HardwareProductsPage() {
  const me = useAuthStore((s) => s.adminUser);
  const isRoot = !!me?.roles?.includes(ADMIN_ROLES.ROOT);

  const { data, loading, error, refetch } = useHardwareProducts();
  const { data: catalogData } = useHardwareCatalogForMapping();
  const [upsert, { loading: saving }] = useUpsertHardwareProduct();
  const [reorder, { loading: reordering }] = useReorderHardwareProducts();
  const [remove] = useDeleteHardwareProduct();

  const [sku, setSku] = useState('');
  const [stripeProductId, setStripeProductId] = useState('');
  const [stripePriceId, setStripePriceId] = useState('');

  const { data: tierData, error: tierError } = useHardwareShippingTiers();
  const [upsertTier, { loading: savingTier }] = useUpsertHardwareShippingTier();
  const [removeTier] = useDeleteHardwareShippingTier();
  const [tierSku, setTierSku] = useState('');
  const [tierQty, setTierQty] = useState('1');
  // Entered in DOLLARS and converted on submit — an operator thinks in dollars, and the
  // wire format is minor units. '0' is a legitimate entry: it means it ships free.
  const [tierAmount, setTierAmount] = useState('');

  const { data: otherData, error: otherError } = useOtherStripeCoupons();
  const [upsertOther, { loading: savingOther }] = useUpsertOtherCoupon();
  const [removeOther] = useDeleteOtherCoupon();
  const [otherKey, setOtherKey] = useState('');
  const [otherStripeId, setOtherStripeId] = useState('');
  const [otherLabel, setOtherLabel] = useState('');
  const [otherTerms, setOtherTerms] = useState('');

  const { data: couponData, error: couponError } = useStripeCoupons();
  const [upsertCoupon, { loading: savingCoupon }] = useUpsertStripeCoupon();
  const [removeCoupon] = useDeleteStripeCoupon();
  const [couponKey, setCouponKey] = useState('');
  const [couponStripeId, setCouponStripeId] = useState('');
  const [couponLabel, setCouponLabel] = useState('');
  const [couponTerms, setCouponTerms] = useState('');
  const [couponRequiresDoc, setCouponRequiresDoc] = useState(false);

  const rows = data?.staffBillingHardwareProducts ?? [];
  const products = catalogData?.staffBillingHardwareCatalog?.products ?? [];
  const tiers = tierData?.staffBillingHardwareShippingTiers ?? [];
  const coupons = couponData?.staffBillingStripeCoupons ?? [];
  const otherCoupons = otherData?.staffBillingOtherCoupons ?? [];

  /** Bands grouped per product, so the table renders one row per SKU. */
  const tiersBySku = useMemo(() => {
    const map = new Map<string, HardwareShippingTier[]>();
    for (const tier of tiers) {
      const existing = map.get(tier.sku);
      if (existing) existing.push(tier);
      else map.set(tier.sku, [tier]);
    }
    return map;
  }, [tiers]);

  /**
   * One entry per distinct Mongo id. The catalog query returns one row per MAPPING (so a
   * product with two configurations appears twice), which is right for the invoice picker
   * but wrong here — this form maps a Mongo id, optionally with a Stripe price.
   */
  const skuOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of rows) counts.set(r.sku, (counts.get(r.sku) ?? 0) + 1);
    const seen = new Set<string>();
    return products
      .filter((p) => (seen.has(p.sku) ? false : (seen.add(p.sku), true)))
      .map((p) => {
        const mapped = counts.get(p.sku) ?? 0;
        // Strip any " (nickname)" suffix the catalog added — this picker is per Mongo id.
        const base = p.stripePriceId ? p.name.replace(/\s*\([^)]*\)\s*$/, '') : p.name;
        return {
          value: p.sku,
          label: base + ' (' + p.sku + ')' + (mapped ? ' — ' + mapped + ' mapped' : ''),
        };
      });
  }, [products, rows]);

  /**
   * The table lists rows in the SAME order the invoice dropdown does, because that is what
   * the arrows move. The catalog query is the authority on that order (it applies
   * `sortOrder` plus its own tie-break), so rows are sequenced by their position in it.
   *
   * That matters most before anyone has ordered anything: every row is at position 0, the
   * dropdown falls back to its derived order, and if the table showed a different sequence
   * the first arrow press would silently commit THAT sequence instead. Rows the catalog
   * doesn't list — an inactive mapping, or one whose Mongo product was retired — cannot be
   * placed and go last, in the order the server sent them.
   */
  const orderedRows = useMemo(() => {
    const position = new Map(products.map((p, i) => [rowKey(p), i]));
    return rows
      .map((row, i) => ({ row, i, at: position.get(rowKey(row)) }))
      .sort(
        (a, b) =>
          (a.at ?? Number.MAX_SAFE_INTEGER) - (b.at ?? Number.MAX_SAFE_INTEGER) ||
          a.i - b.i,
      )
      .map((entry) => entry.row);
  }, [rows, products]);

  if (!isRoot) return <PendingApprovalPage />;

  /**
   * Move one row up or down. Sends the FULL resulting order — the server rewrites positions
   * 1..N from it — so a double-click or a stale tab can't produce a half-applied order.
   */
  const move = async (index: number, direction: -1 | 1) => {
    const next = [...orderedRows];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];

    try {
      await reorder({ variables: { order: next.map(rowKey) } });
      toast.success(
        `Moved ${label(orderedRows[index])} ${direction === -1 ? 'up' : 'down'}`,
      );
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Could not save the new order');
    }
  };



  const valid = !!sku && stripeProductId.trim().startsWith('prod_');

  const save = async () => {
    try {
      const r = await upsert({
        variables: {
          sku,
          stripeProductId: stripeProductId.trim(),
          stripePriceId: stripePriceId.trim(),
          isActive: true,
        },
      });
      const saved = r.data!.staffBillingUpsertHardwareProduct;
      // Echo Stripe's own names back: proof the ids resolved to what they meant.
      toast.success(
        `${label(saved)} → ${saved.stripeProductName || saved.stripeProductId}` +
          (saved.stripePriceNickname ? ` / ${saved.stripePriceNickname}` : '') +
          ' (verified in Stripe)',
      );
      setSku('');
      setStripeProductId('');
      setStripePriceId('');
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Could not save the mapping');
    }
  };

  const toggleActive = async (row: HardwareProductMapping) => {
    try {
      await upsert({
        variables: {
          sku: row.sku,
          stripeProductId: row.stripeProductId,
          stripePriceId: row.stripePriceId,
          isActive: !row.isActive,
        },
      });
      toast.success(`${label(row)} ${row.isActive ? 'deactivated' : 'reactivated'}`);
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Could not update the mapping');
    }
  };

  /**
   * Tick or untick Amazon MCF eligibility.
   *
   * A re-upsert with the flag flipped, exactly as `toggleActive` works — the mapping table has
   * no partial-update mutation, and adding one for a boolean would be a second write path into
   * the same row. The Stripe ids are re-sent unchanged, which is why the server treats an absent
   * `mcfEligible` as "leave it alone": every OTHER save on this page must not disturb this flag.
   *
   * The toast names the consequence rather than the field, because "mcfEligible: true" tells an
   * operator nothing about what just became possible.
   */
  const toggleMcf = async (row: HardwareProductMapping) => {
    try {
      await upsert({
        variables: {
          sku: row.sku,
          stripeProductId: row.stripeProductId,
          stripePriceId: row.stripePriceId,
          isActive: row.isActive,
          mcfEligible: !row.mcfEligible,
        },
      });
      toast.success(
        row.mcfEligible
          ? `${label(row)} removed from Amazon fulfilment — it will no longer be offered on International Requests`
          : `${label(row)} enabled for Amazon fulfilment — it can now be ordered on International Requests`,
      );
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Could not update Amazon eligibility');
    }
  };

  /**
   * Tick or untick China / Houston eligibility (All International). Same re-upsert as
   * `toggleMcf`, sending only this flag so the Amazon one is left as it is.
   */
  const toggleShopifyFulfil = async (row: HardwareProductMapping) => {
    try {
      await upsert({
        variables: {
          sku: row.sku,
          stripeProductId: row.stripeProductId,
          stripePriceId: row.stripePriceId,
          isActive: row.isActive,
          shopifyFulfilEligible: !row.shopifyFulfilEligible,
        },
      });
      toast.success(
        row.shopifyFulfilEligible
          ? `${label(row)} removed from China / Houston — All International will no longer route it there`
          : `${label(row)} enabled for China / Houston — All International can now route it there`,
      );
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Could not update China / Houston eligibility');
    }
  };

  /** Dollars → whole cents. Rejects anything that isn't a clean money figure. */
  const tierAmountMinor = (() => {
    const raw = tierAmount.trim();
    if (!/^\d+(\.\d{1,2})?$/.test(raw)) return null;
    return Math.round(Number(raw) * 100);
  })();
  const tierQtyValue = /^\d+$/.test(tierQty.trim()) ? Number(tierQty.trim()) : 0;
  // 0 is a VALID amount (ships free), so the guard is `!== null`, never truthiness.
  const tierValid = !!tierSku && tierQtyValue >= 1 && tierAmountMinor !== null;

  const saveTier = async () => {
    if (!tierValid) return;
    try {
      await upsertTier({
        variables: {
          sku: tierSku,
          minQuantity: tierQtyValue,
          amountMinor: tierAmountMinor!,
          region: 'us',
          currency: 'usd',
        },
      });
      toast.success(
        `${tierSku} ships for ${money(tierAmountMinor!, 'usd')} from ${tierQtyValue} up`,
      );
      setTierAmount('');
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Could not save the shipping tier');
    }
  };

  const deleteTier = async (tier: HardwareShippingTier) => {
    try {
      await removeTier({
        variables: {
          sku: tier.sku,
          minQuantity: tier.minQuantity,
          region: tier.region,
          currency: tier.currency,
        },
      });
      toast.success(`Removed the ${tier.sku} tier at ${tier.minQuantity}+`);
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Could not remove the shipping tier');
    }
  };

  /**
   * No format check on the coupon id, deliberately: Stripe coupon ids carry no prefix (a
   * `prod_`-style test is impossible), so there is nothing reliable to check locally. The
   * server round-trips the id against this environment's Stripe key before persisting and
   * refuses on 404 with the mode hint, on an invalid coupon, on 403, or with a 503 when the
   * key is unset — so the only client-side gate is "the three required fields are filled".
   */
  const couponValid = !!couponKey.trim() && !!couponStripeId.trim() && !!couponLabel.trim();

  const saveCoupon = async () => {
    if (!couponValid) return;
    try {
      const r = await upsertCoupon({
        variables: {
          couponKey: couponKey.trim(),
          stripeCouponId: couponStripeId.trim(),
          label: couponLabel.trim(),
          terms: couponTerms.trim() || undefined,
          requiresTaxExemptDoc: couponRequiresDoc,
          isActive: true,
        },
      });
      const saved = r.data!.staffBillingUpsertStripeCoupon;
      // Echo Stripe's own name back: proof the id resolved to the coupon they meant.
      toast.success(
        `${saved.couponKey} → ${saved.stripeCouponName || saved.stripeCouponId} (verified in Stripe)`,
      );
      setCouponKey('');
      setCouponStripeId('');
      setCouponLabel('');
      setCouponTerms('');
      setCouponRequiresDoc(false);
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Could not save the coupon');
    }
  };

  /**
   * Deactivate / reactivate is a re-upsert with `isActive` flipped, as the mapping table
   * above does. The whole row goes back — re-sending without `terms` CLEARS it — and the
   * server re-validates the id against Stripe, so a coupon deleted in Stripe since it was
   * added cannot be reactivated.
   */
  const toggleCouponActive = async (row: StripeCouponCatalogEntry) => {
    try {
      await upsertCoupon({
        variables: {
          couponKey: row.couponKey,
          stripeCouponId: row.stripeCouponId,
          label: row.label,
          terms: row.terms ?? undefined,
          requiresTaxExemptDoc: row.requiresTaxExemptDoc,
          isActive: !row.isActive,
        },
      });
      toast.success(
        `${row.couponKey} ${row.isActive ? 'deactivated' : 'reactivated'}`,
      );
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Could not update the coupon');
    }
  };

  /**
   * The root-discount list's save. Same fail-closed round-trip as `saveCoupon`, and the same
   * reason there is no format check on the id: Stripe coupon ids carry no prefix.
   *
   * One extra refusal it can produce, surfaced verbatim: the coupon is already in the
   * approved catalog above. That message names the row to edit, which is the whole fix.
   */
  const otherValid = !!otherKey.trim() && !!otherStripeId.trim() && !!otherLabel.trim();

  const saveOtherCoupon = async () => {
    if (!otherValid) return;
    try {
      const r = await upsertOther({
        variables: {
          couponKey: otherKey.trim(),
          stripeCouponId: otherStripeId.trim(),
          label: otherLabel.trim(),
          terms: otherTerms.trim() || undefined,
          isActive: true,
        },
      });
      const saved = r.data!.staffBillingUpsertOtherCoupon;
      // Echo Stripe's own name back: proof the id resolved to the coupon they meant.
      toast.success(
        `${saved.couponKey} → ${saved.stripeCouponName || saved.stripeCouponId} (verified in Stripe)`,
      );
      setOtherKey('');
      setOtherStripeId('');
      setOtherLabel('');
      setOtherTerms('');
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Could not save the coupon');
    }
  };

  /** Re-upsert with the flag flipped — and the id is re-validated against Stripe on the way. */
  const toggleOtherActive = async (row: OtherCouponCatalogEntry) => {
    try {
      await upsertOther({
        variables: {
          couponKey: row.couponKey,
          stripeCouponId: row.stripeCouponId,
          label: row.label,
          terms: row.terms ?? undefined,
          isActive: !row.isActive,
        },
      });
      toast.success(`${row.couponKey} ${row.isActive ? 'deactivated' : 'reactivated'}`);
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Could not update the coupon');
    }
  };

  const deleteOtherCoupon = async (row: OtherCouponCatalogEntry) => {
    try {
      await removeOther({ variables: { couponKey: row.couponKey } });
      toast.success(`Removed the ${row.couponKey} coupon row`);
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Could not remove the coupon');
    }
  };

  const deleteCoupon = async (row: StripeCouponCatalogEntry) => {
    try {
      await removeCoupon({ variables: { couponKey: row.couponKey } });
      toast.success(`Removed the ${row.couponKey} coupon row`);
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Could not remove the coupon');
    }
  };

  const deleteRow = async (row: HardwareProductMapping) => {
    try {
      await remove({ variables: { sku: row.sku, stripePriceId: row.stripePriceId } });
      toast.success(`Removed the mapping for ${label(row)}`);
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Could not remove the mapping');
    }
  };

  return (
    <div className="flex flex-col gap-4 p-6" data-testid="hardware-products-page">
      <div className="flex flex-col gap-1">
        <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
          <Boxes className="h-6 w-6" />
          {'Hardware Products'}
        </h1>
        <p className="max-w-3xl text-xs text-muted-foreground">
          {
            'Which device-catalog products the Billing Console can invoice, and the existing Stripe product each one bills against. Prices are NOT set here — a row reads the device catalog, or a Stripe price when one is given (for a product sold in configurations at different prices). Ids are per environment, so these are '
          }
          {import.meta.env.PROD ? 'live-mode' : 'test-mode'}
          {' ids.'}
          {' Arrows in the Order column set the order the Create Hardware Only Invoice picker lists these in.'}
        </p>
      </div>

      <div className="flex flex-col gap-3 rounded-lg border bg-card p-4 shadow-sm">
        <h2 className="text-sm font-semibold">{'Map a product'}</h2>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">{'Mongo id'}</Label>
            <Select value={sku} onValueChange={setSku}>
              <SelectTrigger className="cursor-pointer" data-testid="hw-products-sku">
                <SelectValue placeholder="Select a Mongo id" />
              </SelectTrigger>
              <SelectContent>
                {skuOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value} className="cursor-pointer">
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">
              {'The deviceProducts._id from Mongo — re-mapping an existing one repoints it.'}
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hw-product-id" className="text-xs">
              {'Stripe product ID'}
            </Label>
            <Input
              id="hw-product-id"
              value={stripeProductId}
              onChange={(e) => setStripeProductId(e.target.value)}
              placeholder="prod_1ABC..."
              data-testid="hw-products-stripe-id"
            />
            <p className="text-[11px] text-muted-foreground">
              {'Verified against Stripe on save — a price_… id or a wrong-mode id is rejected here.'}
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hw-price-id" className="text-xs">
              {'Stripe price ID'}
            </Label>
            <Input
              id="hw-price-id"
              value={stripePriceId}
              onChange={(e) => setStripePriceId(e.target.value)}
              placeholder="price_1ABC… (optional)"
              data-testid="hw-products-price-id"
            />
            <p className="text-[11px] text-muted-foreground">
              {'Only for a product sold in configurations at different prices. Leave blank to price from the device catalog. The Stripe price needs a nickname.'}
            </p>
          </div>

          <div className="flex items-end">
            <Button
              onClick={save}
              disabled={!valid || saving}
              className="cursor-pointer"
              data-testid="hw-products-save"
            >
              {saving ? 'Verifying…' : 'Save mapping'}
            </Button>
          </div>
        </div>
      </div>

      {error ? (
        <p
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          data-testid="hw-products-error"
        >
          {error.message}
        </p>
      ) : null}

      <Table data-testid="hw-products-table">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[110px]">{'Order'}</TableHead>
            <TableHead>{'Mongo id'}</TableHead>
            <TableHead>{'Configuration'}</TableHead>
            <TableHead>{'Stripe product'}</TableHead>
            <TableHead>{'Status'}</TableHead>
            {/* A SECOND axis, not a second Status. `Status` says whether we sell it; this says
                whether we hold stock of it in an Amazon fulfilment centre — which is what the
                International Requests item picker reads. */}
            <TableHead className="w-[110px]">{'Amazon MCF'}</TableHead>
            {/* A THIRD axis: whether the China factory / Houston warehouse can ship it — what the
                All International China / Houston routes read. */}
            <TableHead className="w-[130px]">{'China / Houston'}</TableHead>
            <TableHead>{'Last updated'}</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading && rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="text-xs text-muted-foreground">
                {'Loading…'}
              </TableCell>
            </TableRow>
          ) : null}
          {!loading && rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="text-xs text-muted-foreground">
                {'Nothing mapped yet — no product can be invoiced until at least one is.'}
              </TableCell>
            </TableRow>
          ) : null}
          {orderedRows.map((row, index) => (
            <TableRow key={rowKey(row)} data-testid={'hw-products-row-' + rowKey(row)}>
              <TableCell className="text-xs">
                <div className="flex items-center gap-1">
                  <span className="w-4 tabular-nums text-muted-foreground">
                    {index + 1}
                  </span>
                  <Button
                    onClick={() => move(index, -1)}
                    variant="ghost"
                    size="icon-xs"
                    disabled={index === 0 || reordering}
                    aria-label={'Move ' + label(row) + ' up'}
                    className="h-6 w-6 cursor-pointer p-0"
                    data-testid={'hw-products-up-' + rowKey(row)}
                  >
                    {'↑'}
                  </Button>
                  <Button
                    onClick={() => move(index, 1)}
                    variant="ghost"
                    size="icon-xs"
                    disabled={index === orderedRows.length - 1 || reordering}
                    aria-label={'Move ' + label(row) + ' down'}
                    className="h-6 w-6 cursor-pointer p-0"
                    data-testid={'hw-products-down-' + rowKey(row)}
                  >
                    {'↓'}
                  </Button>
                </div>
              </TableCell>
              <TableCell className="font-mono text-xs">{row.sku}</TableCell>
              <TableCell className="text-xs">
                {row.stripePriceId ? (
                  <div className="flex flex-col">
                    <span>{row.stripePriceNickname || '—'}</span>
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {'priced by Stripe · ' + row.stripePriceId}
                    </span>
                  </div>
                ) : (
                  <span className="text-muted-foreground">
                    {'priced by the catalog'}
                  </span>
                )}
              </TableCell>
              <TableCell className="text-xs">
                <div className="flex flex-col">
                  <span>{row.stripeProductName || '—'}</span>
                  <span className="font-mono text-[11px] text-muted-foreground">
                    {row.stripeProductId}
                  </span>
                </div>
              </TableCell>
              <TableCell className="text-xs">
                {row.isActive ? 'Active' : 'Inactive'}
              </TableCell>
              {/* A CHECKBOX rather than a Deactivate-style button: this is a property of the
                  product, toggled far more often than the mapping is edited, and the whole
                  point of moving it out of code was that it should be a five-second change. */}
              <TableCell>
                <label className="flex cursor-pointer items-center gap-2 text-xs">
                  <Checkbox
                    checked={row.mcfEligible}
                    onCheckedChange={() => toggleMcf(row)}
                    aria-label={'Amazon MCF for ' + label(row)}
                    data-testid={'hw-products-mcf-' + rowKey(row)}
                  />
                  <span className={row.mcfEligible ? '' : 'text-muted-foreground'}>
                    {row.mcfEligible ? 'Eligible' : '—'}
                  </span>
                </label>
              </TableCell>
              <TableCell>
                <label className="flex cursor-pointer items-center gap-2 text-xs">
                  <Checkbox
                    checked={row.shopifyFulfilEligible}
                    onCheckedChange={() => toggleShopifyFulfil(row)}
                    aria-label={'China / Houston for ' + label(row)}
                    data-testid={'hw-products-shopify-' + rowKey(row)}
                  />
                  <span className={row.shopifyFulfilEligible ? '' : 'text-muted-foreground'}>
                    {row.shopifyFulfilEligible ? 'Eligible' : '—'}
                  </span>
                </label>
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {row.updatedAt ? `${row.updatedAt.slice(0, 10)} · ${row.updatedBy ?? ''}` : '—'}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => toggleActive(row)}
                    variant="ghost"
                    className="cursor-pointer"
                    data-testid={'hw-products-toggle-' + rowKey(row)}
                  >
                    {row.isActive ? 'Deactivate' : 'Reactivate'}
                  </Button>
                  <Button
                    onClick={() => deleteRow(row)}
                    variant="ghost"
                    className="cursor-pointer text-destructive"
                    data-testid={'hw-products-delete-' + rowKey(row)}
                  >
                    {'Remove'}
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/*
        US shipping tiers. Separate from the mapping card above because it answers a
        different question — not "which Stripe product", but "what does freight cost at this
        quantity". A product with NO tier here bills the device catalog's per-device rate,
        which is every product except the ones listed.
      */}
      <div className="flex flex-col gap-3 rounded-lg border bg-card p-4 shadow-sm">
        <div className="flex flex-col gap-1">
          <h2 className="text-sm font-semibold">{'US shipping tiers'}</h2>
          <p className="max-w-3xl text-[11px] text-muted-foreground">
            {
              'For a product whose freight is a quantity band rather than a per-device rate — OptiKiosk 50 ships at $100 for one, $150 for two, free from three. The amount is the WHOLE charge at that quantity, not per device, and a tier applies from its quantity up until a higher one takes over. Enter 0 to ship free. Anything not listed here bills the device catalog rate ($7.90 per device).'
            }
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">{'Mongo id'}</Label>
            <Select value={tierSku} onValueChange={setTierSku}>
              <SelectTrigger className="cursor-pointer" data-testid="hw-tiers-sku">
                <SelectValue placeholder="Select a Mongo id" />
              </SelectTrigger>
              <SelectContent>
                {skuOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value} className="cursor-pointer">
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hw-tier-qty" className="text-xs">
              {'From quantity'}
            </Label>
            <Input
              id="hw-tier-qty"
              value={tierQty}
              onChange={(e) => setTierQty(e.target.value)}
              placeholder="1"
              inputMode="numeric"
              data-testid="hw-tiers-qty"
            />
            <p className="text-[11px] text-muted-foreground">
              {'Applies from this quantity up. The highest tier covers everything above it.'}
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hw-tier-amount" className="text-xs">
              {'Shipping (USD)'}
            </Label>
            <Input
              id="hw-tier-amount"
              value={tierAmount}
              onChange={(e) => setTierAmount(e.target.value)}
              placeholder="100.00"
              inputMode="decimal"
              data-testid="hw-tiers-amount"
            />
            <p className="text-[11px] text-muted-foreground">
              {'Total for the whole shipment at that quantity. 0 ships free.'}
            </p>
          </div>

          <div className="flex items-start pt-6">
            <Button
              onClick={saveTier}
              disabled={!tierValid || savingTier}
              className="cursor-pointer"
              data-testid="hw-tiers-save"
            >
              {savingTier ? 'Saving…' : 'Save tier'}
            </Button>
          </div>
        </div>

        {tierError ? (
          <p
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            data-testid="hw-tiers-error"
          >
            {tierError.message}
          </p>
        ) : null}

        <Table data-testid="hw-tiers-table">
          <TableHeader>
            <TableRow>
              <TableHead>{'Mongo id'}</TableHead>
              <TableHead>{'Tiers'}</TableHead>
              <TableHead>{'Last updated'}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {tiers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-xs text-muted-foreground">
                  {'No shipping tiers — every product bills the device catalog rate per device.'}
                </TableCell>
              </TableRow>
            ) : null}
            {[...tiersBySku.entries()].map(([tierRowSku, skuTiers]) => (
              <TableRow key={tierRowSku} data-testid={'hw-tiers-row-' + tierRowSku}>
                <TableCell className="font-mono text-xs">{tierRowSku}</TableCell>
                <TableCell className="text-xs">{ladder(skuTiers)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {skuTiers[0]?.updatedAt
                    ? `${skuTiers[0].updatedAt.slice(0, 10)} · ${skuTiers[0].updatedBy ?? ''}`
                    : '—'}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap items-center gap-1">
                    {[...skuTiers]
                      .sort((a, b) => a.minQuantity - b.minQuantity)
                      .map((tier) => (
                        <Button
                          key={tier.minQuantity}
                          onClick={() => deleteTier(tier)}
                          variant="ghost"
                          size="sm"
                          className="cursor-pointer text-destructive"
                          data-testid={
                            'hw-tiers-delete-' + tierRowSku + '-' + tier.minQuantity
                          }
                        >
                          {'Remove ' + tier.minQuantity + '+'}
                        </Button>
                      ))}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/*
        Stripe coupons. Third section on this page rather than a route of its own because it
        is the same job as the two above — deciding what the Billing Console is allowed to
        bill against — and it is maintained on the same rare occasions. Nothing here is
        hardware, which is why it carries its own heading rather than joining a table above.

        MAPPING + POLICY ONLY: no percent, no amount, no currency. What a coupon takes off
        lives in Stripe, and a mirror could only drift from the money a customer is charged.
      */}
      <div className="flex flex-col gap-3 rounded-lg border bg-card p-4 shadow-sm">
        <div className="flex flex-col gap-1">
          <h2 className="text-sm font-semibold">{'Stripe coupons'}</h2>
          <p className="max-w-3xl text-[11px] text-muted-foreground">
            {
              'Which Stripe coupons the Billing Console can attach to a customer, discounting all their future subscription invoices. The discount itself lives in Stripe — nothing here sets a percent or an amount. Coupon rows are per database, so these are '
            }
            {/* COPY, not a guard: the same wording as the page header above. Which mode a
                row belongs to is decided by the database it was written to and the Stripe
                key this environment holds, never by this string. */}
            {import.meta.env.PROD ? 'live-mode' : 'test-mode'}
            {' ids.'}
            {' A coupon marked tax-exempt-doc can only be applied when the account has a verified tax-exemption certificate in the Document Center.'}
            {' Read by the Billing Console\u2019s Apply Stripe Coupon card only \u2014 the root-discount list below is a separate table and never appears in that picker.'}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hw-coupon-key" className="text-xs">
              {'Coupon key'}
            </Label>
            <Input
              id="hw-coupon-key"
              value={couponKey}
              // Uppercased as it is typed: the key is a slug (A-Z, 0-9, _ and -) and the
              // server refuses a lowercase one, which is a pointless round-trip to lose.
              onChange={(e) => setCouponKey(e.target.value.toUpperCase())}
              placeholder="NON-PROFIT-10"
              data-testid="hw-coupons-key"
            />
            <p className="text-[11px] text-muted-foreground">
              {'What the Apply Coupon card sends — the Stripe id never leaves this page.'}
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hw-coupon-stripe-id" className="text-xs">
              {'Stripe coupon ID'}
            </Label>
            <Input
              id="hw-coupon-stripe-id"
              value={couponStripeId}
              onChange={(e) => setCouponStripeId(e.target.value)}
              placeholder="Ny1F2mLp"
              data-testid="hw-coupons-stripe-id"
            />
            <p className="text-[11px] text-muted-foreground">
              {'Verified against Stripe on save — a typo or a wrong-mode id is refused here, never stored.'}
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hw-coupon-label" className="text-xs">
              {'Label'}
            </Label>
            <Input
              id="hw-coupon-label"
              value={couponLabel}
              onChange={(e) => setCouponLabel(e.target.value)}
              placeholder="Non-profit 10%"
              data-testid="hw-coupons-label"
            />
            <p className="text-[11px] text-muted-foreground">
              {'How the coupon reads in the Apply Coupon picker.'}
            </p>
          </div>

          <div className="flex items-start pt-6">
            <Button
              onClick={saveCoupon}
              disabled={!couponValid || savingCoupon}
              className="cursor-pointer"
              data-testid="hw-coupons-save"
            >
              {savingCoupon ? 'Verifying…' : 'Save coupon'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="flex flex-col gap-1.5 md:col-span-3">
            <Label htmlFor="hw-coupon-terms" className="text-xs">
              {'Terms'}
            </Label>
            <Input
              id="hw-coupon-terms"
              value={couponTerms}
              onChange={(e) => setCouponTerms(e.target.value)}
              placeholder="10% off, forever, registered non-profits only"
              data-testid="hw-coupons-terms"
            />
            <p className="text-[11px] text-muted-foreground">
              {'The human terms an operator reads before granting it. Optional — re-saving a row without it clears it.'}
            </p>
          </div>

          <div className="flex flex-col gap-1.5 pt-6">
            <label className="flex cursor-pointer items-center gap-2 text-xs">
              <Checkbox
                checked={couponRequiresDoc}
                onCheckedChange={(v) => setCouponRequiresDoc(v === true)}
                className="cursor-pointer"
                data-testid="hw-coupons-requires-doc"
              />
              {'Requires tax-exempt doc'}
            </label>
            <p className="text-[11px] text-muted-foreground">
              {'Apply refuses without a verified, non-archived tax-exemption certificate on the account.'}
            </p>
          </div>
        </div>

        {couponError ? (
          <p
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            data-testid="hw-coupons-error"
          >
            {couponError.message}
          </p>
        ) : null}

        <Table data-testid="hw-coupons-table">
          <TableHeader>
            <TableRow>
              <TableHead>{'Key'}</TableHead>
              <TableHead>{'Coupon'}</TableHead>
              <TableHead>{'Tax-exempt doc'}</TableHead>
              <TableHead>{'Status'}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {coupons.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-xs text-muted-foreground">
                  {'No coupons configured — the Apply Coupon card has nothing to offer until one is.'}
                </TableCell>
              </TableRow>
            ) : null}
            {coupons.map((row) => (
              <TableRow
                key={row.couponKey}
                data-testid={'hw-coupons-row-' + row.couponKey}
              >
                <TableCell className="font-mono text-xs">{row.couponKey}</TableCell>
                <TableCell className="text-xs">
                  <div className="flex flex-col">
                    <span>{row.label}</span>
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {row.stripeCouponId}
                      {row.stripeCouponName ? ' · ' + row.stripeCouponName : ''}
                    </span>
                    {row.terms ? (
                      <span className="text-[11px] text-muted-foreground">
                        {row.terms}
                      </span>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell className="text-xs">
                  {row.requiresTaxExemptDoc ? 'Required' : '—'}
                </TableCell>
                <TableCell className="text-xs">
                  {row.isActive ? 'Active' : 'Inactive'}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => toggleCouponActive(row)}
                      variant="ghost"
                      className="cursor-pointer"
                      data-testid={'hw-coupons-toggle-' + row.couponKey}
                    >
                      {row.isActive ? 'Deactivate' : 'Reactivate'}
                    </Button>
                    <Button
                      onClick={() => deleteCoupon(row)}
                      variant="ghost"
                      className="cursor-pointer text-destructive"
                      data-testid={'hw-coupons-delete-' + row.couponKey}
                    >
                      {'Remove'}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/*
        Root discount coupons. FOURTH section, and a separate table from the one above
        (`billing_other_coupons`, migration 040) rather than a flag on it: the two lists carry
        different rules — a row above can demand a certificate and can never displace an
        existing discount; a row here can do neither and CAN — so a shared table would make
        every read a filter, and one wrong filter offers a row from the wrong list in the
        wrong picker.

        There is no tax-exempt-doc control here, and no column behind one.
      */}
      <div className="flex flex-col gap-3 rounded-lg border bg-card p-4 shadow-sm">
        <div className="flex flex-col gap-1">
          {/*
            DISPLAY COPY. The identifiers do NOT follow it: the table is still
            `billing_other_coupons`, the ops are still `staffBilling*OtherCoupon*`, and the
            testids are still `hw-other-coupons-*`. Same rule as the Apply Coupon card's
            title (BillingConsole.tsx) — a label is allowed to change, a key is not, and
            renaming a testid to match a heading is how a suite goes green while testing
            nothing.
          */}
          <h2 className="text-sm font-semibold">{'Root discount coupons'}</h2>
          <p className="max-w-3xl text-[11px] text-muted-foreground">
            {
              'Read by OptiHub → Early Promoters Coupon (root only), never by the Apply Stripe Coupon card above. These never require a certificate, and an apply from that tab may REPLACE a discount the customer already carries. Rows are per database, so these are '
            }
            {import.meta.env.PROD ? 'live-mode' : 'test-mode'}
            {' ids.'}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hw-other-coupon-key" className="text-xs">
              {'Coupon key'}
            </Label>
            <Input
              id="hw-other-coupon-key"
              value={otherKey}
              // Uppercased as it is typed: the key is a slug and the server refuses a
              // lowercase one, which is a pointless round-trip to lose.
              onChange={(e) => setOtherKey(e.target.value.toUpperCase())}
              placeholder="EARLY-PROMOTERS-20"
              data-testid="hw-other-coupons-key"
            />
            <p className="text-[11px] text-muted-foreground">
              {'What the Early Promoters Coupon tab sends — the Stripe id never leaves this page.'}
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hw-other-coupon-stripe-id" className="text-xs">
              {'Stripe coupon ID'}
            </Label>
            <Input
              id="hw-other-coupon-stripe-id"
              value={otherStripeId}
              onChange={(e) => setOtherStripeId(e.target.value)}
              placeholder="EARLY_PROMOTERS_20PCT_OFF"
              data-testid="hw-other-coupons-stripe-id"
            />
            <p className="text-[11px] text-muted-foreground">
              {'Verified against Stripe on save. Also refused if it is already in the catalog above — one coupon belongs to one list.'}
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hw-other-coupon-label" className="text-xs">
              {'Label'}
            </Label>
            <Input
              id="hw-other-coupon-label"
              value={otherLabel}
              onChange={(e) => setOtherLabel(e.target.value)}
              placeholder="Early promoters 20%"
              data-testid="hw-other-coupons-label"
            />
            <p className="text-[11px] text-muted-foreground">
              {'How the coupon reads in the Early Promoters Coupon picker.'}
            </p>
          </div>

          <div className="flex items-start pt-6">
            <Button
              onClick={saveOtherCoupon}
              disabled={!otherValid || savingOther}
              className="cursor-pointer"
              data-testid="hw-other-coupons-save"
            >
              {savingOther ? 'Verifying…' : 'Save coupon'}
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="hw-other-coupon-terms" className="text-xs">
            {'Terms'}
          </Label>
          <Input
            id="hw-other-coupon-terms"
            value={otherTerms}
            onChange={(e) => setOtherTerms(e.target.value)}
            placeholder="20% off, forever, early promoters cohort"
            data-testid="hw-other-coupons-terms"
          />
          <p className="text-[11px] text-muted-foreground">
            {'The human terms an operator reads before granting it. Optional — re-saving a row without it clears it.'}
          </p>
        </div>

        {otherError ? (
          <p
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            data-testid="hw-other-coupons-error"
          >
            {otherError.message}
          </p>
        ) : null}

        <Table data-testid="hw-other-coupons-table">
          <TableHeader>
            <TableRow>
              <TableHead>{'Key'}</TableHead>
              <TableHead>{'Coupon'}</TableHead>
              <TableHead>{'Status'}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {otherCoupons.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-xs text-muted-foreground">
                  {'No root-discount coupons configured — the Early Promoters Coupon tab has nothing to offer until one is.'}
                </TableCell>
              </TableRow>
            ) : null}
            {otherCoupons.map((row) => (
              <TableRow
                key={row.couponKey}
                data-testid={'hw-other-coupons-row-' + row.couponKey}
              >
                <TableCell className="font-mono text-xs">{row.couponKey}</TableCell>
                <TableCell className="text-xs">
                  <div className="flex flex-col">
                    <span>{row.label}</span>
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {row.stripeCouponId}
                      {row.stripeCouponName ? ' · ' + row.stripeCouponName : ''}
                    </span>
                    {row.terms ? (
                      <span className="text-[11px] text-muted-foreground">{row.terms}</span>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell className="text-xs">
                  {row.isActive ? 'Active' : 'Inactive'}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => toggleOtherActive(row)}
                      variant="ghost"
                      className="cursor-pointer"
                      data-testid={'hw-other-coupons-toggle-' + row.couponKey}
                    >
                      {row.isActive ? 'Deactivate' : 'Reactivate'}
                    </Button>
                    <Button
                      onClick={() => deleteOtherCoupon(row)}
                      variant="ghost"
                      className="cursor-pointer text-destructive"
                      data-testid={'hw-other-coupons-delete-' + row.couponKey}
                    >
                      {'Remove'}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div>
        <Button onClick={() => refetch()} variant="ghost" className="cursor-pointer">
          {'Refresh'}
        </Button>
      </div>
    </div>
  );
}
